import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_DAYS * 86400000) return;

    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream) {
      const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);
      if (isSafari) {
        setIsIOS(true);
        setShowBanner(true);
      }
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowBanner(false);
    if (outcome === 'dismissed') {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-50">
      <div className="flex items-center justify-between max-w-lg mx-auto">
        <div className="flex-1 mr-3">
          <p className="font-semibold text-gray-900 text-sm">Get the SkillsNetPro App</p>
          {isIOS ? (
            <p className="text-xs text-gray-500">Tap Share then "Add to Home Screen"</p>
          ) : (
            <p className="text-xs text-gray-500">Install for quick access from your home screen</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="py-2 px-4 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: '#EF7A2B' }}
            >
              Install
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="py-2 px-4 rounded-lg text-gray-600 text-sm font-medium bg-gray-100"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
